<template>
  <div class="merchant-login-page">
    <div class="unzer-logo-wrapper">
      <img
        :src="unzerLogo"
        alt="Unzer"
        class="unzer-logo"
        data-testid="unzer-logo"
      />
    </div>

    <div class="merchant-login-content">
      <div v-if="error" class="error-message" data-testid="error-message">
        {{ error }}
      </div>

      <div v-if="!error" class="login-card" data-testid="login-card">
        <h1 class="login-title" data-testid="login-title">
          {{ $t('pages.auth.sso_login.title') }}
        </h1>

        <div class="sso-button-section">
          <el-button
            id="sso-login-button"
            class="sso-button"
            tag="a"
            :href="ssoRedirectUrl"
            data-testid="sso-login-button"
            @click="startSsoLogin"
          >
            <img
              :src="unionIcon"
              alt=""
              class="union-icon"
              data-testid="union-icon"
            />
            <span class="sso-button-text" data-testid="sso-button-text">
              {{ $t('pages.auth.sso_login.button') }}
            </span>
          </el-button>
        </div>
      </div>

      <div
        v-if="!error"
        class="need-assistance-section"
        data-testid="assistance-section"
      >
        <p class="assistance-text" data-testid="assistance-text">
          <strong>{{ $t('pages.auth.sso_login.assistance.title') }}</strong>
          {{ $t('pages.auth.sso_login.assistance.contact') }}
          <a
            href="mailto:support@unzer.com"
            class="support-link"
            data-testid="support-email-link"
          >
            support@unzer.com
          </a>
        </p>
      </div>
    </div>

    <div class="login-footer" data-testid="login-footer">
      <div class="footer-left" data-testid="footer-left">
        <a
          :href="privacyPolicyUrl"
          target="_blank"
          class="footer-link"
          data-testid="privacy-policy-link"
        >
          {{ $t('pages.auth.sso_login.footer.privacy_policy') }}
        </a>
        <a
          :href="imprintUrl"
          target="_blank"
          class="footer-link"
          data-testid="imprint-link"
        >
          {{ $t('pages.auth.sso_login.footer.imprint') }}
        </a>
      </div>
      <div class="footer-right" data-testid="footer-right">
        <el-select
          v-model="language"
          class="language-selector"
          placement="top"
          popper-class="language-dropdown-popper"
          data-testid="language-selector"
        >
          <el-option
            v-for="lang in locales"
            :key="lang.value"
            :label="lang.label"
            :value="lang.value"
            :data-testid="`language-option-${lang.value}`"
          />
        </el-select>
      </div>
    </div>
  </div>
</template>

<script>
import { getSSOConfig } from '@uz/config'
import { useKeycloak } from '@josempgon/vue-keycloak'
import {
  getRedirectUri,
  hasPendingLoginResponse
} from '@shared/plugins/keycloak'
import { gastroLocaleSuffix, locales } from '@shared/constants'
import unzerLogo from '@shared/assets/images/unzer-logo-color.svg'
import unionIcon from '@shared/assets/icons/union.svg'
import '@uz/styles/login-sso.css'

export default {
  name: 'SSOLogin',

  data() {
    const config = getSSOConfig()

    return {
      error: config
        ? null
        : this.$t('pages.auth.sso_login.configuration_error'),
      ssoRedirectUrl: config?.redirectUrl || '',
      idpHint: config?.idpHint || '',
      unzerLogo,
      unionIcon
    }
  },

  computed: {
    locales() {
      return locales.map((locale) => ({
        value: locale.value,
        label: locale.label.split(' ')[0]
      }))
    },
    language: {
      get() {
        return this.$i18n.locale || 'de-DE'
      },
      set(newValue) {
        if (this.language === newValue) return
        this.$i18n.locale = newValue
      }
    },
    privacyPolicyUrl() {
      return this.language === 'de-DE'
        ? 'https://www.unzer.com/de/datenschutz/'
        : 'https://www.unzer.com/en/privacy-policy/'
    },
    imprintUrl() {
      return this.language === 'de-DE'
        ? 'https://www.unzer.com/de/impressum/'
        : 'https://www.unzer.com/en/imprint/'
    }
  },

  created() {
    if (!this.$i18n.locale || this.$i18n.locale === 'en-US') {
      this.$i18n.locale = 'de-DE'
    }

    const currentLang = this.$i18n.locale || 'de-DE'
    this.$i18n.locale = currentLang.replace(gastroLocaleSuffix, '')

    // A login response in the fragment belongs to keycloak-js, which reads it
    // during init. The history state is kept because the router navigates by it.
    if (globalThis.location.hash && !hasPendingLoginResponse()) {
      globalThis.history.replaceState(
        globalThis.history.state,
        '',
        globalThis.location.pathname + globalThis.location.search
      )
    }
  },

  methods: {
    // The `href` is an authorize URL this app assembles itself, carrying no
    // `state` and no PKCE verifier, so keycloak-js cannot exchange the code it
    // returns. The adapter's own login can. The href stays as the fallback for
    // when the adapter has not initialised.
    startSsoLogin(event) {
      const { keycloak } = useKeycloak()

      if (!keycloak?.value?.login) {
        return
      }

      event?.preventDefault?.()

      const options = {
        // Without a hint Keycloak asks which provider to use: a detour, but
        // still a login the adapter can finish, unlike the href fallback.
        ...(this.idpHint ? { idpHint: this.idpHint } : {}),
        redirectUri: getRedirectUri()
      }

      try {
        const result = keycloak.value.login(options)
        result?.catch?.(() => {
          globalThis.location.href = this.ssoRedirectUrl
        })
      } catch {
        globalThis.location.href = this.ssoRedirectUrl
      }
    }
  }
}
</script>
